import React, { FC, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Rectangle, Circle, Polygon } from 'react-leaflet';

import { IArea } from '../../../models/types';
import { paths } from '../../Root/paths';
import { AreaTypes, AreaModes, ICoordXY } from '../../../models/types';
import { iRootState, Dispatch } from '../../../store';
import {
  getBoxCenter,
  getBoxBounds,
  getCircleCenter,
  getCircleRadius,
  getPolygonPositions
} from '../utils';
import { mapColor as colors } from '../../../theme';

import AreaListItemTooltip from './AreaListItemTooltip';

const mapState = (state: iRootState) => ({
  activeArea: state.infrastructureAreas.item,
  hoverArea: state.infrastructureAreas.hover,
  alertArea: state.infrastructureAreas.alert,
  isGeo: state.infrastructureMap.layers.isGeo,
  office: state.infrastructureOffices.item,
  mode: state.infrastructureMap.mode,
  editType: state.infrastructureMap.type
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    focusArea: d.infrastructureAreas.focusArea
  };
};

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

type IProps = {
  area: IArea;
} & connectedProps;

const AreaListItem: FC<IProps> = ({
  area,
  activeArea,
  hoverArea,
  alertArea,
  focusArea,
  office,
  isGeo,
  editType,
  mode
}) => {
  const { id, mapId, mapColor, geomType, geomGlobalCoords, geomCoords } = area;

  const position = isGeo ? geomGlobalCoords : geomCoords;

  const history = useHistory();
  const areaEl = useRef<any>();

  useEffect(() => {
    if (activeArea && activeArea.id === id) {
      let center!: ICoordXY;

      switch (geomType) {
        case AreaTypes.Box:
          center = getBoxCenter(position);

          break;
        case AreaTypes.Circle:
          center = getCircleCenter(position);

          break;
        case AreaTypes.Polygon:
          if (areaEl.current?.leafletElement?._bounds) {
            const {
              _southWest: { lat: southWestLat, lng: southWestLng },
              _northEast: { lat: northEastLat, lng: northEastLng }
            } = areaEl.current.leafletElement._bounds;

            const polygonBoxCoordinates = [southWestLng, southWestLat, northEastLng, northEastLat];

            center = getBoxCenter(polygonBoxCoordinates);
          }

          break;
      }

      focusArea(center);
    }
  }, [activeArea]);

  const handleClick = () => {
    history.push(paths.map.office.areas.show(id) + '?mapId=' + mapId);
  };

  const isBox = geomType === AreaTypes.Box;
  const isCircle = geomType === AreaTypes.Circle;
  const isPolygon = geomType === AreaTypes.Polygon;
  const isHover = Boolean(hoverArea && id === hoverArea.id);
  const isAlert = Boolean(alertArea && String(id) === String(alertArea.id));
  const isEditMode = mode === AreaModes.Edit;

  // Скрывать исходную зону:
  //  при редактировании, если выбран другой тип, чем у исходной зоны
  //  при перерисовке (Create) активной зоны — всегда скрываем исходную геометрию
  if (activeArea && activeArea.id === id && (mode === AreaModes.Create || (isEditMode && editType !== geomType))) {
    return null;
  }

  const color = isHover || isAlert ? colors.darkBlueberry : mapColor;
  const opacity = isHover || isAlert ? 0.5 : 0.3;
  const weight = isAlert ? 3 : 2;

  // In edit mode, pmIgnore must be false so Geoman can initialize layer.pm
  // In other modes, pmIgnore=true to prevent Geoman from managing the layer
  const pmIgnore = !isEditMode;

  return (
    <>
      {isBox && (
        <Rectangle
          type={AreaTypes.Box}
          bounds={getBoxBounds(position)}
          weight={weight}
          color={color}
          fillOpacity={opacity}
          onClick={mode === AreaModes.Default ? handleClick : undefined}
          interactive={mode !== AreaModes.Create}
          pmIgnore={pmIgnore}
          areaId={id}
          snapIgnore={true}
        >
          <AreaListItemTooltip area={area} />
        </Rectangle>
      )}
      {isCircle && (
        <Circle
          type={AreaTypes.Circle}
          center={getCircleCenter(position)}
          radius={getCircleRadius(position)}
          weight={weight}
          color={color}
          fillOpacity={opacity}
          onClick={mode === AreaModes.Default ? handleClick : undefined}
          interactive={mode !== AreaModes.Create}
          pmIgnore={pmIgnore}
          areaId={id}
          snapIgnore={true}
        >
          <AreaListItemTooltip area={area} />
        </Circle>
      )}
      {isPolygon && (
        <Polygon
          type={AreaTypes.Polygon}
          positions={getPolygonPositions(position)}
          weight={weight}
          color={color}
          fillOpacity={opacity}
          onClick={mode === AreaModes.Default ? handleClick : undefined}
          interactive={mode !== AreaModes.Create}
          ref={areaEl}
          pmIgnore={pmIgnore}
          areaId={id}
          snapIgnore={true}
        >
          <AreaListItemTooltip area={area} />
        </Polygon>
      )}
    </>
  );
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(AreaListItem);
